import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import ReactHighCharts from 'react-highcharts';
import { useQuery } from 'react-query';
import { ChartPieOutlineIcon } from '../../../../atoms/material-icons';
import { AdvancesDeclines } from '../../api';
import EmptyState from '../empty-state';
import { useSelector } from 'react-redux';
import { isWithinTradingHours } from '../../../../../helpers/utils';
import AdvancesDeclinesTable from './ad-table/advances-declines-table';
import './advances-declines.m.css';

const AdvancesDeclinesWidget = ({ widget = false }) => {
  const [advancesDeclinesData, setAdvancesDeclinesData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);


  const getAdvancesDeclinesData = async () => {
    try {
      const response = await AdvancesDeclines();
      return response.json();
    } catch (error) {
      return console.error(error);
    }
  };

  const resultAdvancesDeclines = useQuery(
    'getAdvancesDeclinesData',
    () => getAdvancesDeclinesData(),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setAdvancesDeclinesData(data?.data);
      },
      enabled: initialLoading || isWithinTradingHours(tradingHolidays, specialTradingDays),
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultAdvancesDeclines?.isSuccess) {
      setAdvancesDeclinesData(resultAdvancesDeclines?.data?.data);
    }
  }, []);

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return (
    <div styleName="advances-declines">
      <div styleName="header-title-container">
        <ChartPieOutlineIcon size={'26px'} color={'#d01e51'} />
        <h4 styleName="list-header-title">Advances & Declines(BSE)</h4>
      </div>

      {!advancesDeclinesData || advancesDeclinesData?.length === 0 ? (
        <div styleName="no-data-container-ext">
          <div styleName="no-data-container-int">
            <EmptyState />
          </div>
        </div>
      ) : (
        <>
          <div styleName="chart-bar">
            <ReactHighCharts
              config={{
                accessibility: {
                  enabled: false,
                },
                exporting: { enabled: false },
                chart: {
                  type: 'pie',
                  spacingTop: 0,
                  spacingBottom: 10,
                  spacingLeft: 0,
                  spacingRight: 0,
                  height: 230,
                  backgroundColor: isDarkMode && widget ? '#242424' : '#ffffff',
                },
                title: {
                  text: '',
                  useHTML: true,
                  widthAdjust: -28,
                },
                colors: ['#DADEE1 ', '#14C588 ', '#d01e51'],
                tooltip: false,
                credits: {
                  enabled: false,
                },
                yAxis: {
                  title: {
                    text: '',
                  },
                },
                plotOptions: {
                  series: {
                    animation: false,
                  },
                  pie: {
                    shadow: false,
                    dataLabels: {
                      distance: 5,
                      connectorWidth: 0,
                    },
                    center: ['50%', '50%'],
                  },
                },
                series: [
                  {
                    data: [
                      {
                        name: 'Unchanged',
                        y: get(advancesDeclinesData, ['chart', 'unchanged'], 0),
                      },
                      {
                        name: 'Advance',
                        y: get(advancesDeclinesData, ['chart', 'adv'], 0),
                      },
                      {
                        name: 'Declines',
                        y: get(advancesDeclinesData, ['chart', 'dec'], 0),
                      },
                    ],
                    size: '80%',
                    dataLabels: {
                      formatter() {
                        return this.y > 0 ? `<b>${this.y}</b> ` + `<br/>${this.point.name}` : null;
                      },
                    },
                  },
                ],
              }}
            />
          </div>
          <div styleName="overall-stats-container">
            <section>
              <span
                styleName={
                  advancesDeclinesData.overall > 0
                    ? 'overall-value positive'
                    : 'overall-value negative'
                }
              >
                {advancesDeclinesData.overall}%
              </span>
              <p styleName="text-header">Overall Market Capitalization</p>
              <p styleName="text-content">{advancesDeclinesData.message}</p>
            </section>
            <section>
              <AdvancesDeclinesTable data={advancesDeclinesData} />
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default AdvancesDeclinesWidget;
